import useTranslation from "next-translate/useTranslation"
import Image from 'next/image'
import { useRouter } from "next/router"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { IPublicStatistics } from "@api/schema"
import { iconUrl } from "@components/common/Icon"
import ProjectCategoryFilterItem from "@components/marketplace/projects/ProjectCategoryFilterItem"
import { loadStatisticsAction } from "@redux/helper/actions"
import { IStatisticsSingleState } from "@redux/helper/reducers"
import { AppState } from "@redux/reducer"
import { StatisticsType } from "@redux/reduxTypes"
import {
  MarketProjectFilterType,
} from "@redux/usecases/marketplace"
import { Routes } from "@services/routes"


interface IProps {
  /** current search params */
  currentFilterItemState: MarketProjectFilterType[]
  /** function to be called when the user clicks one of the filter items */
  onFilterItemChanged: (newProjectTypeFilter: MarketProjectFilterType[]) => void
  /** should extended category item be active? */
  extendedCategoryItemActive?: boolean
  /** should extended category item be visible? */
  extendedCategoryItemVisible?: boolean
}

/**
 * this component provides tabs for filtering projects in the marketplace or for linking to
 * marketplace related pages like the extended category-page
 */
const MarketFilterTabs: React.FC<IProps> = ({
  currentFilterItemState,
  onFilterItemChanged,
  extendedCategoryItemActive = false,
  extendedCategoryItemVisible = false,
}: IProps) => {
  const { t } = useTranslation("common")
  const router = useRouter()
  const dispatch = useDispatch()

  const publicStatistics = useSelector((state: AppState) => state.statistics[StatisticsType.Platform] as IStatisticsSingleState<IPublicStatistics>)

  useEffect(() => {
    if (!publicStatistics?.loaded && !publicStatistics?.isLoading) {
      dispatch(loadStatisticsAction(StatisticsType.Platform))
    }
  }, [])

  // projectFilter-Buttons are active on special situations:
  const ideaButtonChosen = currentFilterItemState?.includes(MarketProjectFilterType.Ideas)
  const planningProjectsButtonChosen = currentFilterItemState?.includes(MarketProjectFilterType.ProjectsInPlanning)
  const runningProjectsButtonChosen = currentFilterItemState?.includes(MarketProjectFilterType.ProjectsRunning)
  const finishedProjectsButtonChosen = currentFilterItemState?.includes(MarketProjectFilterType.ProjectsFinished)

  /**
   * rebuilds the currentFilterItemState
   * depending on the given filter: adds or removes it
   * and toggles Ideas-filter on changing Projects-filter
   *
   * @param filter the MarketProjectFilterType that should be toggled
   * @returns a reorganised array of searchParams.projectTypeFilter
   */
  const rebuildProjectTypeFilterOnButtonClick = (filterType: MarketProjectFilterType): MarketProjectFilterType[] => {
    // deal with the current searchParams or an empty array
    let newProjectTypeFilter: MarketProjectFilterType[] = currentFilterItemState || []
    if (newProjectTypeFilter.includes(filterType)) {
      // remove the given filterType if it was already selected
      newProjectTypeFilter.splice(newProjectTypeFilter.indexOf(filterType), 1)
    } else {
      if (filterType === MarketProjectFilterType.Ideas) {
        // if ideas are chosen remove all other entries
        newProjectTypeFilter = [MarketProjectFilterType.Ideas]
      } else {
        // add the given filterType
        newProjectTypeFilter.push(filterType)
        // remove Ideas if a project-filter is chosen:
        // ideas and projects should not be selected at the same time
        if (newProjectTypeFilter.includes(MarketProjectFilterType.Ideas)) {
          newProjectTypeFilter.splice(newProjectTypeFilter.indexOf(MarketProjectFilterType.Ideas), 1)
        }
      }
    }
    return newProjectTypeFilter
  }

  // type of individual form filter buttons
  interface FormFilterButton {
    textKey: string
    iconURL: string
    filterType: MarketProjectFilterType
    isChosen: boolean
    classNameExtra?: string
    isVisible: boolean
  }

  // list of 4 form filter buttons to be mapped to a ProjectCategoryFilterItem
  const formFilterButtons: FormFilterButton[] = [
    {
      textKey: "ideas",
      iconURL: iconUrl("light-bulb"),
      filterType: MarketProjectFilterType.Ideas,
      isChosen: ideaButtonChosen,
      isVisible: true,
    },
    {
      textKey: "planning",
      iconURL: iconUrl("project"),
      filterType: MarketProjectFilterType.ProjectsInPlanning,
      isChosen: planningProjectsButtonChosen,
      classNameExtra: "grouped-filteritem",
      isVisible: true,
    },
    {
      textKey: "running",
      iconURL: iconUrl("project-implementation"),
      filterType: MarketProjectFilterType.ProjectsRunning,
      isChosen: runningProjectsButtonChosen,
      classNameExtra: "grouped-filteritem",
      isVisible: publicStatistics?.data?.runningProjects > 0,
    },
    {
      textKey: "finished",
      iconURL: iconUrl("check-in-circle"),
      filterType: MarketProjectFilterType.ProjectsFinished,
      isChosen: finishedProjectsButtonChosen,
      classNameExtra: "grouped-filteritem",
      isVisible: publicStatistics?.data?.finishedProjects > 0,
    }
  ]

  return <div className="icon-filteritem-container pb-4 text-center filter-item">
    {extendedCategoryItemVisible === true &&
      // this does not have to be a ProjectCategoryFilterItem, a Link would also be okay; but it's easy...
      <ProjectCategoryFilterItem
        isActive={extendedCategoryItemActive}
        onClick={() => {
          void router.push(Routes.Categories)
        }}
        tooltipText={t("marketplace.filterIcons.categories")}
        isImage
        disabled={extendedCategoryItemActive}
        data-testid={"element-categories"}
      >
        <Image className="icon-filteritem"
          src={iconUrl("flash-light")}
          alt={t("marketplace.filterIcons.categories")}
          layout="intrinsic"
          width={90}
          height={90}
          aria-label={t("marketplace.filterIcons.categories")}
          priority
        />
      </ProjectCategoryFilterItem>
    }
    {publicStatistics?.data?.activeProjects > 0 &&
      formFilterButtons.map((button: FormFilterButton) => button.isVisible &&
        <ProjectCategoryFilterItem key={button.textKey}
          isActive={!extendedCategoryItemActive && button.isChosen}
          // determine whether the current button is the only one that is chosen
          disabled={button.isChosen && (1 === formFilterButtons.filter((b: FormFilterButton) => b.isChosen).length)}
          onClick={() => {
            const newProjectTypeFilter = rebuildProjectTypeFilterOnButtonClick(button.filterType)
            onFilterItemChanged(newProjectTypeFilter)
          }}
          tooltipText={t("marketplace.filterIcons." + button.textKey)}
          isImage
          data-testid={"element-" + button.textKey}
          additionalClassName={button.classNameExtra}
        >
          <Image className="icon-filteritem"
            src={button.iconURL}
            alt={t("marketplace.filterIcons." + button.textKey)}
            layout="intrinsic"
            width={90}
            height={90}
            aria-label={t("marketplace.filterIcons." + button.textKey)}
            priority
          />
        </ProjectCategoryFilterItem>)
    }
  </div>
}

export default MarketFilterTabs
